import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { Buildings, SignIn, EnvelopeSimpleOpen } from "@phosphor-icons/react";
import { PUBLIC_BACKEND as BACKEND, api, formatApiErrorDetail } from "@/lib/api";

/**
 * Courier-branded sign-in page (route: `/c/:slug/login`).
 *
 * Mirrors the visual identity of the existing `/c/:slug/signup` customer
 * signup page so a recipient who lands here from a courier's branded
 * welcome email sees the same logo, name, and tagline they signed up under.
 *
 * The page itself just renders the courier's logo + a standard sign-in form;
 * the actual auth call still hits the shared `/api/auth/login` endpoint via
 * AuthContext, so every existing flow (email-verification gate, forced
 * first-login password change, wrong-tenant detection, etc.) continues to
 * work without modification.
 */
export default function CourierLogin() {
  const { slug } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [courier, setCourier] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/public/courier/${slug}`)
      .then((r) => setCourier(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUnverifiedEmail(null);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back to ${courier?.name || "Kanal"}`);
      navigate(u?.role === "driver" ? "/driver/portal" : "/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setUnverifiedEmail(detail.email || email.toLowerCase());
        toast.error(detail.message || "Please verify your email before signing in.");
      } else if (detail && typeof detail === "object" && detail.code === "wrong_tenant") {
        toast.error(detail.message || "Wrong portal for this account.", { duration: 6000 });
      } else {
        toast.error(detail ? formatApiErrorDetail(detail) : "Login failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resendVerification = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: unverifiedEmail });
      toast.success("Verification email sent — please check your inbox");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="courier-not-found">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 mx-auto flex items-center justify-center mb-4">
            <Buildings size={24} weight="bold" className="text-red-600" />
          </div>
          <h1
            className="text-2xl font-semibold tracking-tight mb-2"
            style={{ fontFamily: "Work Sans" }}
          >
            Courier not found
          </h1>
          <p className="text-zinc-600 mb-6">
            The link you followed doesn&apos;t match any active courier company on Kanal. Please
            check with your courier for the correct sign-in URL.
          </p>
          <Link to="/login">
            <Button variant="outline" className="w-full">Back to Kanal sign-in</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!courier) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-500" data-testid="courier-login-loading">
        Loading courier…
      </div>
    );
  }

  const logoUrl = courier.logo_url ? `${BACKEND}${courier.logo_url}` : null;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="courier-login-page">
      <Toaster position="top-right" />
      <div className="max-w-md mx-auto px-6 py-10">
        {/* Courier branding card — identical visual treatment to signup */}
        <div
          className="bg-white border border-zinc-200 rounded-xl p-6 mb-5 text-center"
          data-testid="courier-brand-card"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={courier.name}
              className="h-16 mx-auto object-contain mb-4"
              data-testid="courier-logo"
            />
          ) : (
            <div className="h-16 w-16 mx-auto bg-[#002BF6] text-white rounded-lg flex items-center justify-center mb-4">
              <Buildings size={28} weight="bold" />
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1">
            Sign in to your account at
          </div>
          <div
            className="text-xl font-semibold text-zinc-900"
            style={{ fontFamily: "Work Sans" }}
            data-testid="courier-name"
          >
            {courier.name}
          </div>
          {courier.tagline && (
            <div className="text-xs text-zinc-500 mt-1">{courier.tagline}</div>
          )}
        </div>

        {/* Sign-in form */}
        <form
          onSubmit={onSubmit}
          className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4"
          data-testid="courier-login-form"
        >
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              autoFocus
              className="mt-1.5"
              data-testid="courier-login-email"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Password
              </Label>
              <Link
                to="/forgot-password"
                className="text-[11px] text-[#002BF6] hover:underline"
                data-testid="courier-login-forgot-link"
              >
                Forgot?
              </Link>
            </div>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              minLength={6}
              className="mt-1.5"
              data-testid="courier-login-password"
            />
          </div>

          {unverifiedEmail && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-900"
              data-testid="courier-login-unverified-banner"
            >
              <div className="flex items-start gap-2">
                <EnvelopeSimpleOpen size={16} weight="bold" className="shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold">Please verify your email first</div>
                  <div className="mt-1">
                    We sent a verification link to <b>{unverifiedEmail}</b>. Click the link and
                    then come back to sign in.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={resendVerification}
                    disabled={resending}
                    className="mt-2"
                    data-testid="courier-login-resend-btn"
                  >
                    {resending ? "Sending…" : "Resend verification email"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white"
            data-testid="courier-login-submit"
          >
            <SignIn size={15} weight="bold" className="mr-1.5" />
            {submitting ? "Signing in…" : `Sign in to ${courier.name}`}
          </Button>
        </form>

        <div className="text-center mt-5 text-xs text-zinc-600">
          Don&apos;t have an account yet?{" "}
          <Link
            to={`/c/${slug}/signup`}
            className="text-[#002BF6] font-semibold hover:underline"
            data-testid="courier-login-signup-link"
          >
            Create one with {courier.name}
          </Link>
        </div>
      </div>
    </div>
  );
}
